import { deepFreeze } from './freeze.ts';
import { createRng } from './rng.ts';
import type { Actor, GameDefinition, Outcome, Rng, RngState, Seat, Viewer } from './types.ts';

export interface MatchConfig<O> {
  readonly seats: number;
  readonly seed: string;
  readonly options?: Partial<O>;
  /** Deep-freeze every state to catch plugin mutation. Default true. */
  readonly freeze?: boolean;
  /** Safety valve against plugins whose autoAction never yields. Default 10_000. */
  readonly maxAutoSteps?: number;
}

export interface LogEntry<A> {
  readonly actor: Actor;
  readonly action: A;
}

/** Everything needed to reconstruct a match deterministically (NFR-DET-02). */
export interface MatchRecord<A, O> {
  readonly gameId: string;
  readonly seats: number;
  readonly seed: string;
  readonly options: O;
  readonly log: readonly LogEntry<A>[];
}

export type RejectReason = 'game-over' | 'not-active' | 'stale-seq' | 'invalid' | 'bad-seat';

export type SubmitResult =
  | { readonly ok: true; readonly seq: number }
  | { readonly ok: false; readonly reason: RejectReason; readonly detail?: string };

export interface Submission<A> {
  readonly seat: Seat;
  readonly action: A;
  /** Expected sequence number (count of accepted player actions so far). */
  readonly seq: number;
}

export interface Match<S, A, V, O> {
  readonly game: GameDefinition<S, A, V, O>;
  readonly state: S;
  readonly seq: number;
  readonly outcome: Outcome | null;
  readonly activeSeats: readonly Seat[];
  readonly waitingOn: readonly Seat[];
  readonly rngState: RngState;
  submit(submission: Submission<A>): SubmitResult;
  view(viewer: Viewer): V;
  record(): MatchRecord<A, O>;
  /** Called after every state change (including auto-actions batches). Returns unsubscribe. */
  subscribe(listener: (match: Match<S, A, V, O>) => void): () => void;
}

export class MatchConfigError extends Error {}

function resolveOptions<O>(game: GameDefinition<unknown, unknown, unknown, O>, partial?: Partial<O>): O {
  return { ...game.defaultOptions, ...(partial ?? {}) } as O;
}

export function createMatch<S, A, V, O>(
  game: GameDefinition<S, A, V, O>,
  config: MatchConfig<O>,
): Match<S, A, V, O> {
  const { seats, seed } = config;
  if (!Number.isInteger(seats) || seats < game.minSeats || seats > game.maxSeats) {
    throw new MatchConfigError(
      `${game.id} supports ${game.minSeats}-${game.maxSeats} seats; got ${seats}`,
    );
  }
  const freeze = config.freeze ?? true;
  const maxAutoSteps = config.maxAutoSteps ?? 10_000;
  const options = resolveOptions(game, config.options);
  const rng: Rng = createRng(seed);
  const log: LogEntry<A>[] = [];
  const listeners = new Set<(m: Match<S, A, V, O>) => void>();

  const seal = (s: S): S => (freeze ? deepFreeze(s) : s);

  let state = seal(game.setup({ seats, options, rng }));
  let seq = 0;

  const runAuto = (): void => {
    if (!game.autoAction) return;
    for (let steps = 0; game.outcome(state) === null; steps++) {
      if (steps >= maxAutoSteps) {
        throw new Error(`${game.id}: autoAction exceeded ${maxAutoSteps} steps`);
      }
      const auto = game.autoAction(state);
      if (auto === null) return;
      state = seal(game.apply(state, auto, { actor: 'system', rng }));
      log.push({ actor: 'system', action: auto });
    }
  };

  runAuto();

  const match: Match<S, A, V, O> = {
    game,
    get state() {
      return state;
    },
    get seq() {
      return seq;
    },
    get outcome() {
      return game.outcome(state);
    },
    get activeSeats() {
      return game.outcome(state) === null ? game.activeSeats(state) : [];
    },
    get waitingOn() {
      if (game.outcome(state) !== null) return [];
      return game.waitingOn ? game.waitingOn(state) : game.activeSeats(state);
    },
    get rngState() {
      return rng.snapshot();
    },
    submit({ seat, action, seq: expected }) {
      if (!Number.isInteger(seat) || seat < 0 || seat >= seats) return { ok: false, reason: 'bad-seat' };
      if (game.outcome(state) !== null) return { ok: false, reason: 'game-over' };
      if (expected !== seq) {
        return { ok: false, reason: 'stale-seq', detail: `expected ${seq}, got ${expected}` };
      }
      if (!game.activeSeats(state).includes(seat)) return { ok: false, reason: 'not-active' };
      const v = game.validate(state, action, seat);
      if (!v.ok) return { ok: false, reason: 'invalid', detail: v.reason };

      state = seal(game.apply(state, action, { actor: seat, rng }));
      log.push({ actor: seat, action });
      seq += 1;
      runAuto();
      for (const l of listeners) l(match);
      return { ok: true, seq };
    },
    view: (viewer) => game.view(state, viewer),
    record: () => ({ gameId: game.id, seats, seed, options, log: [...log] }),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  return match;
}

/**
 * Rebuild state from a record by re-applying the full log (player and system entries)
 * against a fresh RNG with the same seed. Verifies the log is consistent with the rules.
 */
export function replay<S, A, V, O>(game: GameDefinition<S, A, V, O>, record: MatchRecord<A, O>): S {
  if (record.gameId !== game.id) {
    throw new MatchConfigError(`Record is for ${record.gameId}, not ${game.id}`);
  }
  const rng = createRng(record.seed);
  let state = game.setup({ seats: record.seats, options: record.options, rng });
  for (const { actor, action } of record.log) {
    if (actor !== 'system') {
      const v = game.validate(state, action, actor);
      if (!v.ok) throw new Error(`Replay rejected action from seat ${actor}: ${v.reason}`);
    }
    state = game.apply(state, action, { actor, rng });
  }
  return state;
}
